import { useIsTenantAdmin } from "@visioni/a3s-access/app";
import { useSupportUserStatus, type SupportUserStatus } from "@src/features/tenants";
import { FEATURE_FLAGS, useBooleanFlag } from "@src/services/feature-flags/feature-flags";

/**
 * - `"pending"` — the flag or the caller's roles aren't known yet. Callers
 *   should render nothing, NOT deny: denying here bounces a legitimate support
 *   user off a cold deep-link before their roles arrive.
 * - `"granted"` — the flag is on and the caller administers the tenant or is
 *   support staff.
 * - `"denied"` — settled, and one of those is missing.
 */
export type ClipCompilationAccess = "pending" | "granted" | "denied";

/**
 * Single gate for every clip compilation surface — the routes and both nav
 * entry points (web sidebar, native settings menu) — so they cannot drift.
 *
 * Two things this deliberately does NOT do:
 *
 * It does not wait on the feature-flag store reporting itself initialised.
 * `initFeatureFlags` only marks that on its success path, so a failure to
 * construct the Amplitude client leaves it `false` forever; a guard that
 * required it would never resolve and would leave the feature visible to
 * anyone who reached the URL directly. Reading the flag instead fails closed,
 * because an unavailable flag reads as `false`. App boot awaits
 * `initFeatureFlags` before routes mount, so by the time this runs the flag is
 * either loaded or genuinely unavailable.
 *
 * It reads the flag through `useBooleanFlag` rather than `useFeatureFlags()`'s
 * `getBooleanFlag`. The latter is a stable module-level function, so the React
 * Compiler can memoise the call on its identity and freeze the result at
 * whatever it was on first render — see the note in `DumpingSearch.web.tsx`,
 * where that froze a guard `false` on a cold deep-link.
 */
export function useClipCompilationAccess(): ClipCompilationAccess {
  const isEnabled = useBooleanFlag(FEATURE_FLAGS.CLIP_COMPILATION);
  const { isTenantAdmin, isLoading: permissionsLoading } = useIsTenantAdmin();
  const supportStatus = useSupportUserStatus();

  return resolveClipCompilationAccess({ isEnabled, isTenantAdmin, permissionsLoading, supportStatus });
}

export interface ClipCompilationAccessInputs {
  readonly isEnabled: boolean;
  readonly isTenantAdmin: boolean;
  readonly permissionsLoading: boolean;
  readonly supportStatus: SupportUserStatus;
}

/**
 * The decision itself, split out from the hook so it can be unit tested
 * without rendering.
 *
 * `pending` is checked before the flag so that neither a slow permissions
 * lookup nor unsettled auth can be mistaken for a missing entitlement.
 */
export function resolveClipCompilationAccess({
  isEnabled,
  isTenantAdmin,
  permissionsLoading,
  supportStatus,
}: ClipCompilationAccessInputs): ClipCompilationAccess {
  if (permissionsLoading || supportStatus === "pending") return "pending";
  if (!isEnabled) return "denied";
  return isTenantAdmin || supportStatus === "granted" ? "granted" : "denied";
}
